import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useLockedFieldHint } from '../../contexts/LockedFieldHintContext'

interface Option {
  value: string
  label: string
}

interface InlineSelectProps {
  value: string
  options: Option[]
  onChange: (value: string) => void
  placeholder?: string
  fullWidth?: boolean
  className?: string
  buttonClassName?: string
  interactionLocked?: boolean
  lockedHint?: string
}

export default function InlineSelect({
  value,
  options,
  onChange,
  placeholder,
  fullWidth = false,
  className,
  buttonClassName,
  interactionLocked,
  lockedHint
}: InlineSelectProps) {
  const { showAtAnchor } = useLockedFieldHint()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selected = options.find((option) => option.value === value)

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={(e) => {
          if (interactionLocked) {
            if (lockedHint) showAtAnchor(e.currentTarget, lockedHint)
            return
          }
          setOpen((prev) => !prev)
        }}
        className={clsx(
          'flex h-full items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50',
          fullWidth ? 'w-full justify-between rounded-md' : 'rounded-r-md',
          buttonClassName
        )}
      >
        <span className={clsx('font-medium', selected ? 'text-gray-700' : 'text-gray-400')}>
          {selected?.label ?? placeholder ?? 'Select'}
        </span>
        <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div
          className={clsx(
            'absolute right-0 z-20 mt-1 rounded-md border border-gray-200 bg-white shadow-lg',
            fullWidth ? 'w-full' : 'w-20'
          )}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value)
                setOpen(false)
              }}
              className={clsx(
                'w-full px-3 py-2 text-left text-sm hover:bg-gray-50',
                option.value === value && 'text-primary-700 font-semibold'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
